import {ThemeProvider} from "styled-components";
import {catalogFor} from "i18n/catalogs";
import {resolveActiveLocale, type Locale} from "i18n/localePreference";
import {LogoMark} from "nav/BrandMark";
import {isPlainPrimaryClick} from "nav/resourceNavigation";
import {resolveInitialMode} from "theme/resolveInitialMode";
import {darkTheme, lightTheme} from "theme/theme";
import {RouteErrorView} from "./ContentRouteError";
import {ErrorBackdrop, ErrorBrand} from "./RootRouteError.styles";

const THEMES = {light: lightTheme, dark: darkTheme};

/**
 * Lives outside RootLayout. Read the catalog and existing document theme stamp without re-entering
 * any app provider: one of those providers may be the component that threw. React Router continues
 * reporting the original exception to the console; no exception data enters this view.
 */
export function RootRouteError({forcedLocale}: {forcedLocale?: Locale}) {
	const locale = resolveActiveLocale(forcedLocale);
	// The shared registry, never a ternary: a two-branch lookup answered English for every market past
	// the second, and this view is the one place a reader lands when the app itself has failed.
	const messages = catalogFor(locale);
	const stampedMode = typeof document === "undefined" ? null : document.documentElement.dataset.theme ?? null;
	const theme = THEMES[resolveInitialMode(stampedMode, false)];
	const retryHref = typeof window === "undefined" ? "/" : window.location.href;
	return (
		<ThemeProvider theme={theme}>
			<ErrorBackdrop>
				<RouteErrorView
					heading={messages.content.errorHeading}
					body={messages.errors.generic}
					retry={messages.content.errorRetry}
					retryHref={retryHref}
					onRetry={event => {
						if (!isPlainPrimaryClick(event)) return;
						// Preserve unconsumed URL tokens and force a new document even with a fragment.
						event.preventDefault();
						window.location.reload();
					}}
					lang={locale}
				>
					<ErrorBrand><LogoMark aria-hidden="true">T</LogoMark>Transferify</ErrorBrand>
				</RouteErrorView>
			</ErrorBackdrop>
		</ThemeProvider>
	);
}
